import { ReactNode } from 'react';
import { Box, Divider, Stack, Typography } from '@mui/material';

import { cx } from '@/styles/muiTheme';

type Props = {
  title: ReactNode;
  /** Optional one-line description under the title. */
  subtitle?: ReactNode;
  /** Right-aligned content on the title row, e.g. a primary action button. */
  action?: ReactNode;
};

/**
 * The standard admin-page header: title, optional subtitle, and the divider that
 * separates them from the page body.
 *
 * Exists because the pages built on the `Container maxWidth="xl"` template had
 * drifted into five different subtitle treatments (subtitle1, subtitle2, body1,
 * body2 and none), three divider spacings and a stray fontWeight: 700. Sizes and
 * colors come from the theme's h5/body2 rather than per-page sx, so the header
 * tracks the design system from one place.
 */
const PageHeader = ({ title, subtitle, action }: Props) => (
  <>
    <Stack
      direction="row"
      sx={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        <Typography variant="h5" sx={{ color: cx.text.primary }}>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="body2" sx={{ color: cx.text.tertiary }}>
            {subtitle}
          </Typography>
        ) : null}
      </Box>
      {action}
    </Stack>
    <Divider sx={{ marginTop: 3, marginBottom: 3 }} />
  </>
);

export default PageHeader;
